import React, { useContext, useEffect, useState } from "react";
import intl from "react-intl-universal";
import styled from "styled-components";
import { mdiArrowExpandAll } from "@mdi/js";

import ixusUtils from "../../../../../../script/utils/ixusUtils";

import { MainItem } from "./MainItem";
import { Grid } from "../../../../../components/Grid";
import { Button } from "../../../../../components/Button";
import { Spacer } from "../../../../../components/Spacer";
import { BoxList } from "../../../../../components/BoxList";
import { EntryContext } from "../../../../../contexts/EntryContext";
import { IxusResultTransformDialog } from "./IxusResultTransformDialog";

const LayerStyle = styled.div`
    display: ${props => (props.$show ? "flex" : "none")};
`;

export const MainLayer = props => {
    const { show, plot, selectedIndex, setSelectedIndex } = props;

    const entryContext = useContext(EntryContext);
    const { entry } = entryContext;
    const { plans } = entry;
    const ixusSetting = entry.ixusSetting || {};
    const ixusAreas = ixusSetting.ixusAreas || [];

    const viewLayerName = "viewLayer";

    useEffect(() => {
        plot.addLayer(viewLayerName, 10);
    }, []);

    useEffect(() => {
        if (!show) {
            return;
        }
        const planByKey = new Map(plans.map(plan => [plan.key, plan]));
        const kept = ixusAreas.filter(area => planByKey.has(area?.planKey));
        plans.forEach(plan => {
            const existing = kept.find(a => a.planKey === plan.key);
            if(!existing && !plan.id) {
                kept.push({
                    planKey: plan.key,
                    sideView: false,
                    angle: null,
                    heightTop: 0,
                    heightBottom: 0,
                });
            }
        });
        ixusSetting.ixusAreas = kept;
        entryContext.setEntry(entry);
    }, [show, JSON.stringify(entry.plans.map(p => p.key))]);

    const drawPlan = async plan => {
        const viewLayer = plot.getLayer(viewLayerName);
        await ixusUtils.drawPlanAndResult(plan, ixusAreas, plot, viewLayer);
    };

    const redrawSelected = index => {
        const layer = plot.getLayer(viewLayerName);
        layer.removeObjects();
        layer.activate();
        const ixusArea = ixusAreas[index];
        plans.forEach(plan => {
            if (plan.key === ixusArea.planKey) {
                drawPlan(plan);
            }
        });
    };

    // Redraws whenever the selected area's result changes, not just on selection, so a
    // recalculation pushed in via SSE shows up without leaving and re-entering the dataset
    // (service-desk#1660).
    useEffect(() => {
        if (!show || selectedIndex === undefined || !ixusAreas[selectedIndex]) {
            return;
        }
        redrawSelected(selectedIndex);
    }, [show, selectedIndex, ixusAreas[selectedIndex]?.calculation?.resultAvailable]);

    const onSelect = index => {
        setSelectedIndex(index);
    };

    const [transform, setTransform] = useState();

    // Compares the result image's current placement with the one drawResult computed and
    // reports the difference in meters (canvas y points down, plot y up) and as scale factors.
    const showTransform = () => {
        const image = plot.getLayer(viewLayerName).getObjects().find(obj => obj.mxwellname === "ixusArea");
        if (!image) {
            return;
        }
        const result = {
            offsetX: (image.left - image.originalLeft) * plot.refScale,
            offsetY: -(image.top - image.originalTop) * plot.refScale,
            scaleX: image.scaleX / image.originalScaleX,
            scaleY: image.scaleY / image.originalScaleY,
        };
        // eslint-disable-next-line no-console
        console.log("ixus result transform", {
            ...result,
            original: { left: image.originalLeft, top: image.originalTop, scaleX: image.originalScaleX, scaleY: image.originalScaleY },
            current: { left: image.left, top: image.top, scaleX: image.scaleX, scaleY: image.scaleY },
        });
        setTransform(result);
    };

    return (
        <LayerStyle $show={show}>
            {show &&
                <>
                    <Grid columns="1">
                        <Button
                            svgIcon={mdiArrowExpandAll}
                            disabled={selectedIndex === undefined || !ixusAreas[selectedIndex]?.calculation?.ixusResult}
                            onClick={() => showTransform()}
                            value={intl.get("entry.ixus.main.transform")} />
                    </Grid>
                    <Spacer variant="horizontal" />
                </>
            }
            {show &&
                <BoxList
                    onSelect={onSelect}
                    selectedIndex={selectedIndex}>
                    {ixusAreas.map((area, index) =>
                        <MainItem
                            key={index}
                            index={index}
                            ixusArea={area}/>
                    )}
                </BoxList>
            }
            {transform &&
                <IxusResultTransformDialog
                    transform={transform}
                    onClose={() => setTransform(undefined)} />
            }
        </LayerStyle>
    );
};